import type { Logger } from '@amp/web-apps-logger';
import type { JetActionLike, JetLike } from '../types';

export type ClickActionConfig<T extends JetActionLike> = {
    isExternalAction: (action: T) => boolean;
    isCompoundExternalAction?: (action: T) => boolean;
};

type ClickActionReturnType<T extends JetActionLike> = {
    update: (action: T | null) => void;
    destroy: () => void;
};

export type ClickAction<T extends JetActionLike> = (
    node: HTMLElement,
    action: T | null,
) => ClickActionReturnType<T>;

export type DOMClickActionHandler<T extends JetActionLike> = (
    e: Event,
    action: T | null,
) => void;

/**
 * Creates a DOM click event handler called `DOMClickActionHandler` for performing jet actions for `on:click` events.
 * This function should only be called on setup.
 */
export function makeDOMClickActionHandler<
    A extends JetActionLike,
    J extends JetLike,
>(
    jet: J,
    log: Logger,
    { isExternalAction, isCompoundExternalAction }: ClickActionConfig<A>,
): DOMClickActionHandler<A> {
    const handleClickAction = (e: Event, action: A | null) => {
        if (!action) {
            return;
        }

        if (!isExternalAction(action) && !isCompoundExternalAction?.(action)) {
            e.preventDefault();
        }

        e.stopPropagation();
        log.info('dispatching:', action);

        jet.perform(action);
    };

    return handleClickAction;
}

/**
 * Creates a svelte action called `ClickAction` for performing jet actions on click.
 * This function should only be called on setup.
 */
export function makeClickAction<A extends JetActionLike, J extends JetLike>(
    jet: J,
    log: Logger,
    { isExternalAction, isCompoundExternalAction }: ClickActionConfig<A>,
): ClickAction<A> {
    const clickAction = (node: HTMLElement, action: A | null) => {
        let listener: (e: Event) => void;

        function destroy() {
            if (listener) {
                node.removeEventListener('click', listener);
            }
        }

        function update(action: A | null) {
            destroy();

            if (action === null) {
                return;
            }

            listener = (e: Event) => {
                if (
                    !isExternalAction(action) &&
                    !isCompoundExternalAction?.(action)
                ) {
                    e.preventDefault();
                }

                e.stopPropagation();
                log.info('dispatching:', action);

                jet.perform(action);
            };

            node.addEventListener('click', listener);
        }

        update(action);

        return {
            update,
            destroy,
        };
    };

    return clickAction;
}
